import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SingleValue } from 'react-select';

import { useQueryClient } from '@tanstack/react-query';
import { FormikProvider, useFormik } from 'formik';
import { isEqual, sortBy } from 'lodash';

import { Input, MultiSelect, Restricted, SingleSelect } from '@/common';
import { ErrorPage } from '@/components/common/ErrorPage';
import { Breadcrumb, Button, Label, LABEL_TYPE, Spinner, ToggleSwitch } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { PAGE_SIZE } from '@/constants';
import { AuthContext, RolesAndPermissionContext } from '@/contexts';
import { PERMISSION_TYPE, PRIVATE_PAGE_ROUTES, UserType } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import { createUpdateUserSchema } from '@/pages/Users/UserValidation';
import { UsersIcon } from '@/resources';
import { BuildingsIcon, MonitorMobileIcon } from '@/resources/icons/sidebar';
import {
  useCreateUploaderUserMutation,
  useCreateUserMutation,
  useGetAllDevices,
  useGetAllEvents,
  useGetAllOrganizations,
  useGetUserByIdQuery,
  useUpdateUserMutation
} from '@/services';
import {
  CreateEditOrganization,
  Device,
  Organization,
  OrganizationBasic,
  RoleType,
  SelectOptionProps
} from '@/types';

export const CreateEditUser = () => {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userId } = useParams();
  const { currentUserData: currentUser, permissions } = useContext(AuthContext);
  const { roles } = useContext(RolesAndPermissionContext);
  const [searchParams] = useSearchParams();

  const [selectedOrganization, setSelectedOrganization] = useState<SelectOptionProps[]>();
  const [selectedDevices, setSelectedDevices] = useState<SelectOptionProps[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<SelectOptionProps | null>(null);
  const [showOrganization, setShowOrganization] = useState<boolean>(false);
  const [showEvent, setShowEvent] = useState<boolean>(false);
  const [showDevices, setShowDevices] = useState<boolean>(false);
  const [hideOtherInfo, setHideOtherInfo] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<SelectOptionProps | null>(null);
  const [error, setError] = useState<number>();
  const [searchOrganization, setSearchOrganization] = useState<string>('');

  const hasGlobalAccess = permissions.includes(PERMISSION_TYPE.GLOBAL_ACCESS);
  const isEditPage = !!userId;

  if (isEditPage && currentUser && Number(userId) === currentUser.id) {
    navigate(PRIVATE_PAGE_ROUTES.USERS);
  }
  const label = isEditPage ? t('editUser') : t('addUser');
  const isUploader = searchParams.get('isUploader');
  const organizationId = searchParams.get('organizationId');
  const eventId = searchParams.get('eventId');
  const isLockedUploaderRoute = Boolean(isUploader && organizationId && eventId);

  const {
    data: allOrganizations,
    isLoading: isLoadingOrganizations,
    fetchNextPage,
    hasNextPage
  } = useGetAllOrganizations(PAGE_SIZE, searchOrganization, true, undefined);

  const {
    data: user,
    isLoading: isLoadingUserData,
    isRefetching
  } = useGetUserByIdQuery(Number(userId), {
    onSuccess: () => undefined,
    onError: (error) => {
      setError(error.status);
    }
  });

  const { mutate: createUser, isLoading } = useCreateUserMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('userCreated'));
      isUploader && eventId
        ? navigate(PRIVATE_PAGE_ROUTES.SINGLE_EVENT.replace(':eventId', eventId.toString()))
        : navigate(PRIVATE_PAGE_ROUTES.USERS);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
    }
  });

  const { mutate: createUploaderUser, isLoading: isLoadingUploader } =
    useCreateUploaderUserMutation(queryClient, {
      onSuccess: () => {
        showToast(ToastType.SUCCESS, t('userCreated'));
        isUploader && eventId
          ? navigate(PRIVATE_PAGE_ROUTES.SINGLE_EVENT.replace(':eventId', eventId.toString()))
          : navigate(PRIVATE_PAGE_ROUTES.USERS);
      },
      onError: (error) => {
        showToast(ToastType.ERROR, t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
      }
    });

  const getAdditionalValues = () => {
    let organizationIds: number[] = [];
    if (
      [
        UserType.SuperManager,
        UserType.Manager,
        UserType.Creator,
        UserType.Uploader,
        UserType.Controller,
        UserType.Speaker
      ].includes(UserType[selectedRole?.label as keyof typeof UserType]) &&
      user
    ) {
      organizationIds = user?.data?.organizations?.map((org: Organization) => org.id) || [];
    } else if (hasGlobalAccess) {
      organizationIds = currentUser?.organizations?.map((org) => org.id) || [];
    }

    const eventId =
      showEvent && user ? { eventId: user?.data?.events[0]?.id } : { eventId: undefined };

    return { organizationIds, ...eventId };
  };

  const formik = useFormik({
    enableReinitialize: isEditPage,
    initialValues: {
      id: user?.data.id,
      enabled: user?.data.enabled,
      firstName: user?.data.firstName || '',
      lastName: user?.data.lastName || '',
      email: user?.data.email || '',
      roleId: user?.data.role.id || '',
      deviceIds: user?.data.devices.map((device: Device) => device?.id) || [],
      isMfaForced: user?.data.isMfaForced ?? false,
      ...getAdditionalValues()
    },
    validationSchema: createUpdateUserSchema(showOrganization, showEvent, showDevices),

    onSubmit: (values) => {
      const payload = {
        id: values.id,
        enabled: values.enabled,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        roleId: values.roleId,
        organizationIds:
          (selectedOrganization?.length ? selectedOrganization.map((org) => org.value) : null) ||
          (values.organizationIds?.length ? values.organizationIds : null) ||
          [],
        eventId: values.eventId,
        deviceIds: values?.deviceIds,
        isMfaForced: values.isMfaForced
      };

      if (isEditPage) {
        updateUser(payload);
      } else {
        if (isUploader && eventId) {
          createUploaderUser(payload);
        } else {
          createUser(payload);
        }
      }
    }
  });

  const {
    isLoading: isLoadingEvents,
    isFetching: isFetchingEvents,
    data: allEvents
  } = useGetAllEvents(
    PAGE_SIZE,
    undefined,
    undefined,
    undefined,
    true,
    formik.values?.organizationIds && Array.isArray(formik.values.organizationIds)
      ? formik.values.organizationIds
      : undefined,
    showEvent
  );

  const {
    data: allDevices,
    isLoading: isLoadingDevices,
    isFetching: isFetchingDevices
  } = useGetAllDevices(
    PAGE_SIZE,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    showDevices
  );

  const { mutate: updateUser, isLoading: isUpdating } = useUpdateUserMutation(queryClient, {
    onSuccess: () => {
      navigate(`/users/${userId}`);
      showToast(ToastType.SUCCESS, t('userUpdated'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
    }
  });

  const handleRoleChange = (newValue: SingleValue<SelectOptionProps>) => {
    const role: SelectOptionProps = newValue as SelectOptionProps;

    setShowOrganization(
      [
        UserType.SuperManager,
        UserType.Manager,
        UserType.Creator,
        UserType.Uploader,
        UserType.Controller,
        UserType.Speaker
      ].includes(UserType[role?.label as keyof typeof UserType]) &&
        (hasGlobalAccess ||
          (currentUser?.organizations.length ? currentUser.organizations.length > 1 : true))
    );
    setShowEvent(role?.label === UserType.Uploader);
    setShowDevices(role?.label === UserType.Controller || role?.label === UserType.Speaker);
    if (currentUser?.organizations.length === 1) {
      formik.setFieldValue('organizationIds', [currentUser.organizations[0].id]);
      setSelectedOrganization([
        {
          value: currentUser.organizations[0].id,
          label: currentUser.organizations[0].name
        }
      ]);
    } else {
      setSelectedOrganization([]);
    }
    setSelectedEvent(null);
    setSelectedDevices([]);
    setSelectedRole(role);

    if (
      hasGlobalAccess ||
      (currentUser?.organizations.length ? currentUser.organizations.length > 1 : true)
    ) {
      formik.setFieldValue('organizationIds', []);
    }

    formik.setFieldValue('eventId', role?.label === UserType.Uploader ? '' : undefined);
    formik.setFieldValue(
      'deviceIds',
      role?.label === UserType.Controller || role?.label === UserType.Speaker ? '' : undefined
    );

    formik.setFieldValue('roleId', role?.value);
  };

  const handleOrganizationChange = (newValue: SelectOptionProps[]) => {
    setSelectedOrganization(newValue);
    formik.setFieldValue('organizationIds', newValue?.map((org) => org.value) || []);
  };

  const handleEventChange = (newValue: SingleValue<SelectOptionProps>) => {
    const event: SelectOptionProps = newValue as SelectOptionProps;
    setSelectedEvent(event);
    formik.setFieldValue('eventId', event?.value);
  };

  const filteredDevices: SelectOptionProps[] = useMemo(() => {
    const allDeviceItems = allDevices?.pages.flatMap((page) => page.data?.items) || [];
    const organizationIds = formik.values.organizationIds;

    return allDeviceItems
      .filter(
        (device) =>
          !organizationIds?.length ||
          device.organizations?.some((org: OrganizationBasic) => organizationIds.includes(org.id))
      )
      .map((device) => ({
        value: device.id,
        label: device.name
      }));
  }, [formik.values.organizationIds, allDevices]);

  const getDeviceOptions = () => filteredDevices;

  const getEventOptions = () => {
    return (allEvents?.pages || [])
      .flatMap((page) => page.data?.items)
      .map((event) => ({
        value: event.id,
        label: event.name
      })) as SelectOptionProps[];
  };

  const getOrganizationOptions = () => {
    return (allOrganizations?.pages || []).flatMap((page) =>
      page.data?.items.map((org: CreateEditOrganization) => ({
        value: org.id,
        label: org.name
      }))
    ) as SelectOptionProps[];
  };

  const getRolesOptions = () => {
    return roles.map((role: RoleType) => ({
      value: role.id,
      label: role.name
    })) as SelectOptionProps[];
  };

  const handleOrgRemove = (org: SelectOptionProps) => {
    if (selectedOrganization?.includes(org)) {
      const updated = selectedOrganization.filter((t) => t?.value !== org?.value);
      setSelectedOrganization(updated);

      formik.setFieldValue('organizationIds', updated?.map((item) => item.value) || []);
    }
  };

  const handleDeviceRemove = (device: SelectOptionProps) => {
    if (selectedDevices?.includes(device)) {
      const updated = selectedDevices.filter((t) => t?.value !== device?.value);
      setSelectedDevices(updated);

      formik.setFieldValue('deviceIds', updated?.map((item) => item.value) || []);
    }
  };

  useEffect(() => {
    if (isEditPage && user) {
      const roleOption = {
        value: user?.data.role.id,
        label: user.data.role.name
      };

      setSelectedRole(roleOption);

      handleRoleChange(roleOption);

      setSelectedOrganization(
        user.data.organizations?.map((org: { id: number; name: string }) => ({
          value: org.id,
          label: org.name
        })) || []
      );
      setSelectedEvent({
        value: user.data.events[0]?.id,
        label: user.data.events[0]?.name
      });
      setSelectedDevices(
        user?.data?.devices.map((d: Device) => ({ value: d?.id, label: d?.name }))
      );

      const initial = {
        id: user.data.id,
        enabled: user.data.enabled,
        firstName: user.data.firstName || '',
        lastName: user.data.lastName || '',
        email: user.data.email || '',
        roleId: user.data.role.id,
        deviceIds: user.data.devices.map((d: Device) => d.id),
        organizationIds: user.data.organizations?.map((org: Organization) => org.id) || [],
        eventId: user.data.events[0]?.id,
        isMfaForced: user.data.isMfaForced ?? false
      };

      formik.setValues(initial);
      formik.resetForm({ values: initial });
    }
  }, [user]);

  useEffect(() => {
    if (
      searchParams.get('isUploader') &&
      searchParams.get('organizationId') &&
      searchParams.get('eventId')
    ) {
      setHideOtherInfo(true);

      const role = getRolesOptions()?.find((r) => isUploader && r?.label === UserType.Uploader);
      handleRoleChange(role ?? null);
      isUploader && formik.setFieldValue('eventId', Number(eventId));
      isUploader && formik.setFieldValue('organizationIds', [Number(organizationId)]);
    }
  }, [
    searchParams,
    allEvents?.pages.length,
    allOrganizations?.pages.length,
    roles?.length,
    organizationId,
    eventId
  ]);

  useEffect(() => {
    if (!isEditPage) {
      const hasCreateUser = permissions.includes(PERMISSION_TYPE.CREATE_USER);
      const hasCreateUploaderUser = permissions.includes(PERMISSION_TYPE.CREATE_UPLOADER_USER);
      const isUploaderRoute = isUploader && organizationId && eventId;

      if (!hasCreateUser && hasCreateUploaderUser && !isUploaderRoute) {
        navigate('/users');
      }
    }
  }, [isEditPage, permissions, isUploader, organizationId, eventId, navigate]);

  useEffect(() => {
    if (selectedRole && selectedRole.value !== formik.values.roleId) {
      formik.setFieldValue('roleId', selectedRole.value);
    }
  }, [selectedRole, formik.values.roleId]);

  useEffect(() => {
    const savedOrganizationIds = user?.data.organizations?.map((org: Organization) => org.id) || [];
    const hasSavedOrganizations =
      isEditPage && isEqual(sortBy(formik.values.organizationIds), sortBy(savedOrganizationIds));

    if (formik.values.eventId && allEvents && !isLoadingEvents && !isFetchingEvents) {
      if (isLockedUploaderRoute) {
        return;
      }

      const isSavedEvent =
        hasSavedOrganizations && formik.values.eventId === user?.data.events?.[0]?.id;

      const eventExists =
        isSavedEvent || getEventOptions().some((event) => event.value === formik.values.eventId);
      if (!eventExists) {
        formik.setFieldValue('eventId', selectedRole?.label === UserType.Uploader ? '' : undefined);
        setSelectedEvent(null);
      }
    }
    if (formik.values.deviceIds?.length > 0) {
      if (formik.values.organizationIds?.length === 0) {
        setSelectedDevices([]);
        formik.setFieldValue('deviceIds', []);
      } else if (allDevices && !isLoadingDevices && !isFetchingDevices) {
        const savedDeviceIds = hasSavedOrganizations
          ? user?.data.devices.map((d: Device) => d.id) || []
          : [];
        const validDeviceIds = [...filteredDevices.map((d) => d.value), ...savedDeviceIds];
        const filteredDeviceIds = formik.values.deviceIds.filter((id: number) =>
          validDeviceIds.includes(id)
        );
        if (filteredDeviceIds.length !== formik.values.deviceIds.length) {
          formik.setFieldValue('deviceIds', filteredDeviceIds);
          setSelectedDevices((prev) => prev.filter((d) => filteredDeviceIds.includes(d.value)));
        }
      }
    }
  }, [formik.values.organizationIds, allEvents, filteredDevices, isLockedUploaderRoute]);

  return (
    <div className='flex h-full justify-center overflow-y-auto'>
      {(isEditPage && isLoadingUserData) || isRefetching ? (
        <Spinner />
      ) : error ? (
        <ErrorPage code={error} />
      ) : (
        <div className='flex w-full flex-col md:mx-20 md:mt-7'>
          <Breadcrumb
            icon={<UsersIcon className='size-[17px]' />}
            iconPath={'/users'}
            path={[{ label: label }]}
            className='hidden md:block'
          />
          <div className='mx-3 mt-5 flex flex-col md:mt-9'>
            <Label text={label} type={LABEL_TYPE.H1} className='text-xl md:text-4xl' />
            <Label
              text={t('generalInformation')}
              type={LABEL_TYPE.H3_BOLD}
              className='my-5 !text-lg md:my-9 md:!text-xl'
            />
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit} className='flex w-full flex-col justify-between'>
                <div className='grid w-full grid-cols-1 gap-x-9 md:grid-cols-2 md:gap-y-5'>
                  {isEditPage && (
                    <>
                      <SingleSelect
                        id='enabled'
                        disabled={user?.data?.registeredAt === undefined}
                        containerClassName={`
                          ${user?.data?.registeredAt === undefined ? 'opacity-50 pointer-events-none' : ''}
                        `}
                        options={[
                          { label: t('enabled'), value: true },
                          { label: t('disabled'), value: false }
                        ]}
                        value={{
                          value: formik.values.enabled,
                          label: formik.values.enabled ? t('enabled') : t('disabled')
                        }}
                        handleChange={(value) => formik.setFieldValue('enabled', value?.value)}
                        label={
                          t('availability') +
                          (user?.data?.registeredAt === undefined
                            ? ' (' + t('userNotRegistered') + ')'
                            : '')
                        }
                      />
                      <div />
                    </>
                  )}
                  <Input
                    id='firstName'
                    label={t('firstName')}
                    type='text'
                    isRequired
                    errorMessage={
                      formik.touched.firstName ? (formik.errors.firstName as string) : undefined
                    }
                    isInvalid={!!formik.errors.firstName && !!formik.touched.firstName}
                  />
                  <Input
                    id='lastName'
                    label={t('lastName')}
                    type='text'
                    isRequired
                    errorMessage={
                      formik.touched.lastName ? (formik.errors.lastName as string) : undefined
                    }
                    isInvalid={!!formik.errors.lastName && !!formik.touched.lastName}
                  />
                  {!isEditPage && (
                    <Input
                      id='email'
                      label={t('email')}
                      type='email'
                      isRequired
                      disabled={isEditPage}
                      errorMessage={
                        formik.touched.email ? (formik.errors.email as string) : undefined
                      }
                      isInvalid={!!formik.errors.email && !!formik.touched.email}
                    />
                  )}
                  {!isEditPage && (
                    <div className='flex flex-col gap-1.5 pt-1'>
                      <Label
                        text={t('mfaForced')}
                        type={LABEL_TYPE.PARAGRAPH1}
                        className='text-secondary-gray'
                      />
                      <div className='flex items-center gap-3'>
                        <ToggleSwitch
                          toggled={formik.values.isMfaForced}
                          handleToggle={() =>
                            formik.setFieldValue('isMfaForced', !formik.values.isMfaForced)
                          }
                        />
                        <Label
                          text={formik.values.isMfaForced ? t('mfaForcedOn') : t('mfaForcedOff')}
                          type={LABEL_TYPE.PARAGRAPH1}
                          className='!text-primary-black'
                        />
                      </div>
                    </div>
                  )}
                </div>
                {!hideOtherInfo && (
                  <>
                    <div className='mb-5 mt-1 h-px w-full bg-secondary-blue-gray md:mb-9 md:mt-5' />
                    <div className='flex flex-col gap-y-5 md:gap-y-9'>
                      <Label
                        text={t('otherInformation')}
                        type={LABEL_TYPE.H3_BOLD}
                        className='!text-lg md:!text-xl'
                      />
                      <div className='grid w-full grid-cols-1 gap-x-9 gap-y-4 md:grid-cols-2 md:gap-y-9'>
                        <SingleSelect
                          isClearable={false}
                          id='roleId'
                          label={t('role')}
                          isRequired
                          placeholder={t('selectRole')}
                          options={getRolesOptions()}
                          handleChange={(value) => {
                            handleRoleChange(value);
                          }}
                          value={selectedRole}
                          errorMessage={formik.errors.roleId as string}
                          isInvalid={!!formik.errors.roleId && !!formik.touched.roleId}
                          onBlur={() => formik.setFieldTouched('roleId', true)}
                        />
                        {currentUser?.organizations.length !== 1 && (
                          <Restricted to={[PERMISSION_TYPE.CREATE_USER]}>
                            {showOrganization && (
                              <div className='flex w-full flex-col'>
                                <MultiSelect
                                  className='w-full'
                                  id='organizationIds'
                                  label={t('organizations')}
                                  placeholder={t('selectOrganizations')}
                                  isRequired
                                  disabled={isLoadingOrganizations}
                                  options={isLoadingOrganizations ? [] : getOrganizationOptions()}
                                  handleChange={(value) => {
                                    handleOrganizationChange(value);
                                  }}
                                  controlShouldRenderValue={false}
                                  selectedValues={selectedOrganization as SelectOptionProps[]}
                                  fetchNextPage={fetchNextPage}
                                  hasNextPage={hasNextPage}
                                  isSearchable={true}
                                  errorMessage={formik.errors.organizationIds as string}
                                  isInvalid={
                                    !!formik.errors.organizationIds &&
                                    !!formik.touched.organizationIds
                                  }
                                  onBlur={() => formik.setFieldTouched('organizationIds', true)}
                                  handleInputChange={(inputValue: string) =>
                                    setSearchOrganization(inputValue)
                                  }
                                  showTags
                                  handleTagRemove={handleOrgRemove}
                                  tagIcon={BuildingsIcon}
                                />
                              </div>
                            )}
                          </Restricted>
                        )}
                        <>
                          {showEvent && (
                            <SingleSelect
                              isClearable={false}
                              id='eventId'
                              label={t('event')}
                              placeholder={t('selectEvent')}
                              isRequired
                              options={isLoadingEvents ? [] : getEventOptions()}
                              handleChange={(value) => {
                                handleEventChange(value);
                              }}
                              value={selectedEvent}
                              errorMessage={formik.errors.eventId as string}
                              isInvalid={!!formik.errors.eventId && !!formik.touched.eventId}
                              onBlur={() => formik.setFieldTouched('eventId', true)}
                            />
                          )}
                        </>
                        <>
                          {showDevices && (
                            <div className='flex w-full flex-col'>
                              <MultiSelect
                                className='w-full'
                                label={t('devices')}
                                id='deviceIds'
                                isRequired
                                placeholder={t('selectDevices')}
                                options={isLoadingDevices ? [] : getDeviceOptions()}
                                handleChange={(value) => {
                                  setSelectedDevices(value);
                                  formik.setFieldValue(
                                    'deviceIds',
                                    value.map((m) => m?.value)
                                  );
                                }}
                                controlShouldRenderValue={false}
                                selectedValues={selectedDevices ?? []}
                                errorMessage={formik.errors.deviceIds as string}
                                isInvalid={!!(formik.errors.deviceIds && formik.touched.deviceIds)}
                                onBlur={() => formik.setFieldTouched('deviceIds', true)}
                                showTags
                                handleTagRemove={handleDeviceRemove}
                                tagIcon={MonitorMobileIcon}
                              />
                            </div>
                          )}
                        </>
                      </div>
                    </div>
                  </>
                )}
                <div className='my-6 flex w-full justify-end sm:my-9'>
                  <Button
                    type='submit'
                    variant={ButtonVariant.PRIMARY}
                    disabled={
                      isUpdating ||
                      isLoading ||
                      isLoadingUploader ||
                      !(formik.dirty && formik.isValid)
                    }
                    className='w-full md:w-auto'>
                    {isEditPage ? t('saveChanges') : t('addUser')}
                  </Button>
                </div>
              </form>
            </FormikProvider>
          </div>
        </div>
      )}
    </div>
  );
};
